import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Clock, ArrowLeft, Loader2, CheckCircle, Star, BadgeCheck, Lock, Music2, ExternalLink, Sparkles } from 'lucide-react';
import { ArtistImage } from '../components/ArtistImage';
import { Badge } from '../components/ui/badge';
import { fadeInUp } from '../lib/animations';
import { ReviewsSection, AudioSamplePlayer } from '../components/artist-profile';
import { BookingDialog, BookingSidebar } from '../components/artist-profile';

export default function ArtistProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agent');
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { addToCart, cart } = useCart();
  const [artist, setArtist] = useState(null);
  const [weeklyPrice, setWeeklyPrice] = useState(null); // override from Weekly Specials, if any
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addToCartOpen, setAddToCartOpen] = useState(false);
  const [dubMessage, setDubMessage] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [addingToCart, setAddingToCart] = useState(false);
  const [startingConversation, setStartingConversation] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  const isArtist = user?.role === 'artist';
  const isAdmin = user?.role === 'admin';
  const canSeePrices = isAuthenticated && !isArtist;
  const canBook = isAuthenticated && !isArtist && !isAdmin;
  const canMessage = isAuthenticated && !isArtist;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artistRes, reviewsRes] = await Promise.all([api.getArtist(id), api.getArtistReviews(id)]);
        setArtist(artistRes.data);
        setReviews(reviewsRes.data);

        // If buyer arrived via Weekly Specials, fetch the agent's override price so
        // the booking sidebar shows the price the buyer will actually be charged.
        if (agentId && isAuthenticated && !isArtist) {
          try {
            const ws = await api.getWeeklySpecials();
            const match = (ws.data?.artists || []).find(a => a.id === id && a.agent_id === agentId);
            if (match) setWeeklyPrice(match.price);
          } catch (e) {
            // Non-fatal: buyer sees the artist's regular price.
            console.warn('Could not fetch weekly specials price for artist', id, e);
          }
        }

        // Detect if the viewer is the artist that owns this profile — enables
        // the "Reply publicly" button on their own reviews.
        if (isArtist) {
          try {
            const own = await api.getArtistProfile();
            if (own?.data?.id === id) setIsOwnProfile(true);
          } catch (_) { /* not owner or lookup failed — reply UI stays hidden */ }
        }
      } catch (err) {
        setError('Artist not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, agentId, isAuthenticated, isArtist]);

  const effectivePrice = weeklyPrice != null ? weeklyPrice : artist?.price;

  const handleAddToCart = async () => {
    if (!songTitle.trim()) { toast.error('Please enter the song title'); return; }
    if (!dubMessage.trim()) { toast.error('Please enter a message for your dubplate'); return; }
    setAddingToCart(true);
    const result = await addToCart(id, dubMessage, agentId, songTitle);
    setAddingToCart(false);
    if (result.success) {
      toast.success('Added to cart!', {
        description: agentId
          ? `${artist.name} (Weekly Specials) added to cart`
          : `${artist.name} dubplate added`,
        action: { label: 'View Cart', onClick: () => navigate('/cart') }
      });
      setAddToCartOpen(false);
      setDubMessage('');
      setSongTitle('');
    } else {
      toast.error(result.error);
    }
  };

  const handleMessageArtist = async () => {
    if (!isAuthenticated) { navigate('/login', { state: { returnTo: `/artists/${id}` } }); return; }
    setStartingConversation(true);
    try {
      const response = await api.startConversation(id);
      navigate(`/dashboard?tab=inbox&conversation=${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    } finally {
      setStartingConversation(false);
    }
  };

  const renderStars = (rating) => Array.from({ length: 5 }).map((_, i) => (
    <Star key={`star-${i}`} className={`w-4 h-4 ${i < rating ? 'text-lime fill-lime' : 'text-[#333]'}`} />
  ));

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-lime" /></div>;
  }

  if (error || !artist) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 text-center"><h1 className="text-2xl font-heading font-bold mb-4">Artist Not Found</h1><Link to="/" className="text-lime hover:underline">Back to Artists</Link></div>
      </div>
    );
  }

  const isInCart = cart?.items?.some(item => item.artist_id === id);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <ArtistImage src={artist.image_url} alt={artist.name} name={artist.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />Back to Artists
            </Link>
          </div>
        </div>
      </section>

      {/* Artist Info */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div {...fadeInUp} transition={{ duration: 0.5 }}>
                {agentId && (
                  <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lime/10 border border-lime/30 text-xs font-medium text-lime" data-testid="weekly-specials-banner">
                    <Sparkles className="w-3.5 h-3.5" />
                    Recording this week — booked via Weekly Specials
                  </div>
                )}
                <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 flex items-center gap-3" data-testid="artist-name">
                  {artist.name}
                  {artist.is_verified && <Badge className="bg-blue-500/10 text-blue-400 border-0"><BadgeCheck className="w-4 h-4 mr-1" />Verified</Badge>}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  {canSeePrices ? (
                    <span className="price-tag text-lg">${artist.price.toFixed(2)}</span>
                  ) : (
                    <span className="price-tag text-lg flex items-center gap-1"><Lock className="w-4 h-4" />{isArtist ? 'Artist account' : 'Login to see price'}</span>
                  )}
                  <div className="flex items-center gap-2 text-[#A1A1AA]"><Clock className="w-4 h-4" /><span>{artist.delivery_days} days delivery</span></div>
                  {artist.total_orders > 0 && <div className="flex items-center gap-2 text-[#A1A1AA]"><CheckCircle className="w-4 h-4 text-green-500" /><span>{artist.total_orders} completed</span></div>}
                  {canSeePrices && artist.average_rating && (
                    <div className="flex items-center gap-2">
                      <div className="flex">{renderStars(Math.round(artist.average_rating))}</div>
                      <span className="text-[#A1A1AA]">{artist.average_rating} ({artist.total_reviews} reviews)</span>
                    </div>
                  )}
                </div>

                <AudioSamplePlayer sampleUrl={artist.sample_audio_url ? api.getAudioSampleUrl(artist.sample_audio_url) : null} />

                <div className="mt-8">
                  <h2 className="font-heading text-2xl font-bold mb-4">About</h2>
                  <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-line">{artist.bio}</p>
                </div>

                {artist.spotify_url && (
                  <a
                    href={artist.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-6 text-green-500 hover:text-green-400 transition-colors"
                    data-testid="spotify-link"
                  >
                    <Music2 className="w-5 h-5" />
                    Listen on Spotify
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}

                {/* Features */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { title: 'Custom Message', desc: 'Tell the artist exactly what you want them to say' },
                    { title: 'High Quality Audio', desc: 'Professional studio-quality recording' },
                    { title: 'Secure Payment', desc: 'Funds held until delivery is confirmed' },
                    { title: '45-Day Guarantee', desc: 'Full refund if not delivered on time' }
                  ].map((feature, index) => (
                    <div key={feature.title} className="p-4 bg-surface rounded-xl border border-[#262626]">
                      <h4 className="font-bold mb-1">{feature.title}</h4>
                      <p className="text-sm text-[#A1A1AA]">{feature.desc}</p>
                    </div>
                  ))}
                </div>

                <ReviewsSection
                  reviews={reviews}
                  canSeePrices={canSeePrices}
                  artistId={id}
                  artistUserId={isOwnProfile ? user?.id : null}
                />
              </motion.div>
            </div>

            {/* Sidebar */}
            <BookingSidebar
              artist={artist}
              effectivePrice={effectivePrice}
              originalPrice={weeklyPrice != null ? artist.price : null}
              weeklyOverride={weeklyPrice != null}
              canBook={canBook}
              canSeePrices={canSeePrices}
              canMessage={canMessage}
              isArtist={isArtist}
              isInCart={isInCart}
              onBookClick={() => setAddToCartOpen(true)}
              startingConversation={startingConversation}
              onMessageClick={handleMessageArtist}
            />
          </div>
        </div>
      </section>

      <BookingDialog
        open={addToCartOpen}
        onOpenChange={setAddToCartOpen}
        artist={artist}
        effectivePrice={effectivePrice}
        originalPrice={weeklyPrice != null ? artist.price : null}
        weeklyOverride={weeklyPrice != null}
        dubMessage={dubMessage}
        setDubMessage={setDubMessage}
        songTitle={songTitle}
        setSongTitle={setSongTitle}
        onAddToCart={handleAddToCart}
        addingToCart={addingToCart}
        isInCart={isInCart}
      />

      <Footer />
    </div>
  );
}
